import type { Cents } from "../domain/money";
import type { DealAssumptions, DealCapitalSource, DealStrategy, MaoTargetKind } from "../domain/deal";

/**
 * DEAL UNDERWRITING (spec §87) — pure, deterministic, explainable.
 *
 * Everything is integer cents; ratios are basis points (pct × 100). Every
 * headline figure carries the lines that add up to it, so the screen and the
 * report can expand "Projected profit" into exactly the arithmetic behind it.
 *
 * FIVE THINGS THAT ARE NEVER CONFUSED:
 *   total project cost  — every dollar the deal consumes, borrowed or not;
 *   cash required       — the part that has to come from you;
 *   profit              — sale price minus total project cost;
 *   cash flow           — rent after operating costs AND debt service;
 *   return on cash      — profit (or cash flow) over cash required.
 *
 * FINANCING FOLLOWS THE AMOUNT BORROWED, NEVER THE FACILITY LIMIT. A $60,000
 * standby line with nothing drawn costs nothing but what its terms say for
 * being open (fees / points on the limit when the user says so).
 *
 * Conventions, stated once and shown in the assumptions list:
 *   - interest accrues monthly at rate ÷ 12 on the balance outstanding that
 *     month, rounded half-up to the cent;
 *   - "spread" draws are equal monthly steps over the holding period, each
 *     drawn at the start of its month;
 *   - an amortizing payment is the standard level payment on the balance
 *     when amortization begins;
 *   - selling costs are paid out of the sale proceeds (not from your cash);
 *   - maximum-offer calculations hold the amounts borrowed as entered.
 */

export const DEAL_ENGINE_VERSION = "87.1";

export interface MathLine {
  label: string;
  cents: Cents | null;
  /** How the line participates in its total. */
  op: "+" | "−" | "=" | "·";
  note?: string;
}

export interface Explained {
  cents: Cents | null;
  lines: MathLine[];
}

export interface Ratio {
  /** Basis points (1234 = 12.34%). */
  bps: number | null;
  formula: string;
}

export function mulBps(cents: Cents, bps: number): Cents {
  const raw = (cents * bps) / 10000;
  return raw >= 0 ? Math.floor(raw + 0.5) : -Math.floor(-raw + 0.5);
}

export function ratioBps(numerator: Cents | null, denominator: Cents | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null;
  const raw = (numerator * 10000) / denominator;
  return raw >= 0 ? Math.floor(raw + 0.5) : -Math.floor(-raw + 0.5);
}

const round = (value: number): Cents => (value >= 0 ? Math.floor(value + 0.5) : -Math.floor(-value + 0.5));

/* ------------------------------------------------------------------ */
/* Loans                                                               */
/* ------------------------------------------------------------------ */

/** Standard level monthly payment. Zero rate divides evenly. */
export function levelPayment(principalCents: Cents, annualRatePctX100: number, months: number): Cents {
  if (principalCents <= 0 || months <= 0) return 0;
  if (annualRatePctX100 === 0) return Math.ceil(principalCents / months);
  const r = annualRatePctX100 / 10000 / 12;
  return round((principalCents * r) / (1 - Math.pow(1 + r, -months)));
}

export function monthlyInterest(balanceCents: Cents, annualRatePctX100: number): Cents {
  return round((balanceCents * annualRatePctX100) / 120000);
}

export interface LoanMonth {
  month: number;
  openingBalanceCents: Cents;
  drawCents: Cents;
  interestCents: Cents;
  principalCents: Cents;
  paymentCents: Cents;
  closingBalanceCents: Cents;
  interestOnly: boolean;
}

export interface LoanOutcome {
  sourceId: string;
  name: string;
  kind: DealCapitalSource["kind"];
  limitCents: Cents | null;
  borrowedCents: Cents;
  /** Limit minus borrowed; null when no limit was stated. */
  availableCreditCents: Cents | null;
  pointsCents: Cents;
  feesCents: Cents;
  interestCents: Cents;
  principalPaidCents: Cents;
  balanceAtExitCents: Cents;
  /** Payment in the month after the holding period (what a hold would pay next). */
  nextPaymentCents: Cents;
  nextPaymentInterestOnly: boolean;
  /** Level payment once any interest-only period has ended (null when never amortizing). */
  amortizingPaymentCents: Cents | null;
  rateKnown: boolean;
  schedule: LoanMonth[];
}

/**
 * Walks one loan month by month through the holding period and one month
 * beyond (so a hold knows its next payment). Draws follow drawTiming; the
 * limit plays no part in interest.
 */
export function runLoan(source: DealCapitalSource, holdMonths: number): LoanOutcome {
  const rate = source.ratePctX100 ?? 0;
  const borrowed = Math.max(0, source.amountCents);
  const spread = source.drawTiming === "spread" && holdMonths > 0;
  const schedule: LoanMonth[] = [];
  let balance = 0;
  let drawn = 0;
  let amortPayment: Cents | null = null;
  let interestTotal = 0;
  let principalTotal = 0;
  const months = holdMonths + 1;
  for (let month = 1; month <= months; month += 1) {
    let draw = 0;
    if (month === 1 && !spread) draw = borrowed;
    if (spread && month <= holdMonths) {
      draw = month === holdMonths ? borrowed - drawn : Math.floor(borrowed / holdMonths);
    }
    drawn += draw;
    const opening = balance + draw;
    const interest = monthlyInterest(opening, rate);
    const interestOnly = source.amortizationMonths === null || month <= source.interestOnlyMonths;
    let payment = interest;
    let principal = 0;
    if (!interestOnly && opening > 0) {
      if (amortPayment === null) amortPayment = levelPayment(opening, rate, source.amortizationMonths!);
      payment = Math.min(amortPayment, opening + interest);
      principal = payment - interest;
    }
    const closing = opening - principal;
    schedule.push({
      month,
      openingBalanceCents: opening,
      drawCents: draw,
      interestCents: interest,
      principalCents: principal,
      paymentCents: payment,
      closingBalanceCents: closing,
      interestOnly,
    });
    if (month <= holdMonths) {
      interestTotal += interest;
      principalTotal += principal;
    }
    balance = closing;
  }
  const exit = holdMonths === 0 ? borrowed : schedule[holdMonths - 1].closingBalanceCents;
  const next = schedule[holdMonths];
  const amortizing =
    source.amortizationMonths === null
      ? null
      : amortPayment ?? levelPayment(exit, rate, source.amortizationMonths);
  const pointsBase = source.pointsOn === "limit" ? source.limitCents ?? borrowed : borrowed;
  return {
    sourceId: source.id,
    name: source.name,
    kind: source.kind,
    limitCents: source.limitCents,
    borrowedCents: borrowed,
    availableCreditCents: source.limitCents === null ? null : Math.max(0, source.limitCents - borrowed),
    pointsCents: source.pointsPctX100 === null ? 0 : mulBps(pointsBase, source.pointsPctX100),
    feesCents: Math.max(0, source.feesCents),
    interestCents: interestTotal,
    principalPaidCents: principalTotal,
    balanceAtExitCents: exit,
    nextPaymentCents: next.paymentCents,
    nextPaymentInterestOnly: next.interestOnly,
    amortizingPaymentCents: amortizing,
    rateKnown: source.ratePctX100 !== null || borrowed === 0,
    schedule,
  };
}

/* ------------------------------------------------------------------ */
/* Analysis                                                            */
/* ------------------------------------------------------------------ */

export interface DealAnalysisInput {
  strategy: DealStrategy;
  purchasePriceCents: Cents | null;
  arvCents: Cents | null;
  livingAreaSqftX100: number | null;
  assumptions: DealAssumptions;
  capital: DealCapitalSource[];
}

export interface CapitalSummary {
  loans: LoanOutcome[];
  borrowedCents: Cents;
  facilityLimitCents: Cents | null;
  availableCreditCents: Cents | null;
  cashEnteredCents: Cents;
}

export interface FlipResult {
  salePriceCents: Cents | null;
  acquisition: Explained;
  renovation: Explained;
  financing: Explained;
  holding: Explained;
  disposition: Explained;
  totalProjectCost: Explained;
  cashRequired: Explained;
  profit: Explained;
  margin: Ratio;
  roi: Ratio;
  returnOnCash: Ratio;
  annualizedReturnOnCash: Ratio;
  breakEvenSalePriceCents: Cents | null;
  breakEvenPurchasePriceCents: Cents | null;
  cashBackAtSale: Explained;
  holdMonths: number;
}

export interface RentalResult {
  scheduledAnnualRentCents: Cents | null;
  vacancyAnnualCents: Cents | null;
  effectiveAnnualRentCents: Cents | null;
  operatingExpenses: Explained;
  noiAnnual: Explained;
  capRate: Ratio;
  permanentDebtCents: Cents;
  monthlyDebtService: Explained;
  /** When a payment is still interest-only at stabilization, the payment after it ends. */
  debtServiceAfterInterestOnlyCents: Cents | null;
  monthlyCashFlow: Explained;
  annualCashFlowCents: Cents | null;
  dscr: Ratio;
  cashInvested: Explained;
  cashOnCash: Ratio;
  stabilizedValueCents: Cents | null;
  stabilizedEquity: Explained;
  ltv: Ratio;
  breakEvenRentCents: Cents | null;
}

export interface RefinanceResult {
  ltvPctX100: number;
  stabilizedValueCents: Cents;
  maximumModeledDebtCents: Cents;
  existingDebtCents: Cents;
  refinanceCostsCents: Cents;
  additionalProceeds: Explained;
  newMonthlyPaymentCents: Cents | null;
  monthlyCashFlowAfterRefinanceCents: Cents | null;
  cashLeftInDealCents: Cents | null;
  cashOnCashAfterRefinance: Ratio;
  propertyEquityAfterRefinanceCents: Cents;
}

export interface DealAnalysis {
  engineVersion: string;
  strategy: DealStrategy;
  /** Inputs the analysis needs and does not have yet, in plain words. */
  missing: string[];
  assumptions: string[];
  capital: CapitalSummary;
  flip: FlipResult | null;
  rental: RentalResult | null;
  refinance: RefinanceResult | null;
  heuristicMaoCents: Cents | null;
  targetMao: MaoResult | null;
  rehabPerSqftCents: Cents | null;
}

export interface MaoResult {
  kind: MaoTargetKind;
  target: number;
  maximumPurchasePriceCents: Cents | null;
  explanation: string;
}

const z = (value: Cents | null | undefined): Cents => value ?? 0;
const line = (label: string, cents: Cents | null, op: MathLine["op"], note?: string): MathLine =>
  note === undefined ? { label, cents, op } : { label, cents, op, note };

function summarizeCapital(capital: DealCapitalSource[], holdMonths: number): CapitalSummary {
  const loans = capital.filter((s) => s.kind !== "cash").map((s) => runLoan(s, holdMonths));
  const limits = loans.map((l) => l.limitCents);
  const anyLimit = limits.some((l) => l !== null);
  return {
    loans,
    borrowedCents: loans.reduce((sum, l) => sum + l.borrowedCents, 0),
    facilityLimitCents: anyLimit ? limits.reduce<number>((sum, l) => sum + (l ?? 0), 0) : null,
    availableCreditCents: anyLimit ? loans.reduce((sum, l) => sum + (l.availableCreditCents ?? 0), 0) : null,
    cashEnteredCents: capital.filter((s) => s.kind === "cash").reduce((sum, s) => sum + Math.max(0, s.amountCents), 0),
  };
}

/**
 * Compounding a short, highly leveraged return to a year produces figures
 * like 3,900% that are arithmetic, not information — so an annualized
 * figure is offered only for holds of at least a month whose return on
 * cash is between −100% and +100%.
 */
function annualize(bps: number | null, months: number): number | null {
  if (bps === null || months <= 0 || bps <= -10000 || bps > 10000) return null;
  const value = Math.pow(1 + bps / 10000, 12 / months) - 1;
  if (!Number.isFinite(value)) return null;
  return round(value * 10000);
}

export function analyzeDeal(input: DealAnalysisInput): DealAnalysis {
  const a = input.assumptions;
  const missing: string[] = [];
  const assumptions: string[] = [
    "Interest is charged monthly on the amount borrowed (rate ÷ 12), never on an unused limit.",
    "Selling costs are paid from the sale proceeds.",
  ];
  const holdMonths = a.holdMonths ?? 0;
  if (a.holdMonths === null) missing.push("How many months you expect to hold the property before selling or renting");
  const capital = summarizeCapital(input.capital, holdMonths);
  if (capital.loans.some((l) => !l.rateKnown)) {
    missing.push("An interest rate for every loan with money borrowed");
    assumptions.push("A loan without a rate is modelled with no interest until one is entered.");
  }
  if (input.capital.some((s) => s.kind !== "cash" && s.drawTiming === "spread")) {
    assumptions.push("Loans marked \"drawn over the hold\" are borrowed in equal monthly steps.");
  }
  const purchase = input.purchasePriceCents;
  if (purchase === null) missing.push("Purchase price");

  /* ---- uses of money common to both strategies ---- */
  const contingency = a.contingencyPctX100 === null ? 0 : mulBps(z(a.rehabCents), a.contingencyPctX100);
  const acquisition: Explained = {
    cents: purchase === null ? null : purchase + z(a.buyerClosingCostsCents),
    lines: [line("Purchase price", purchase, "+"), line("Buyer closing costs", z(a.buyerClosingCostsCents), "+")],
  };
  if (a.rehabCents === null) missing.push("A rehab estimate (even a rough one)");
  const renovation: Explained = {
    cents: z(a.rehabCents) + contingency,
    lines: [
      line("Rehab estimate", a.rehabCents, "+", a.rehabProvenance || undefined),
      line(`Contingency${a.contingencyPctX100 === null ? "" : ` (${(a.contingencyPctX100 / 100).toFixed(1)}% of rehab)`}`, contingency, "+"),
    ],
  };
  const holdingMonthly =
    round(z(a.taxesAnnualCents) / 12) + round(z(a.insuranceAnnualCents) / 12) + z(a.utilitiesMonthlyCents) + z(a.hoaMonthlyCents) + z(a.otherHoldingMonthlyCents);
  const holding: Explained = {
    cents: holdingMonthly * holdMonths,
    lines: [
      line("Property taxes (annual ÷ 12)", round(z(a.taxesAnnualCents) / 12), "·", "per month"),
      line("Insurance (annual ÷ 12)", round(z(a.insuranceAnnualCents) / 12), "·", "per month"),
      line("Utilities", z(a.utilitiesMonthlyCents), "·", "per month"),
      line("HOA", z(a.hoaMonthlyCents), "·", "per month"),
      line("Other carrying costs", z(a.otherHoldingMonthlyCents), "·", "per month"),
      line(`Monthly carrying cost × ${holdMonths} months`, holdingMonthly * holdMonths, "="),
    ],
  };
  const financingLines: MathLine[] = [];
  let financingCost = 0;
  let principalPaid = 0;
  for (const loan of capital.loans) {
    financingCost += loan.pointsCents + loan.feesCents + loan.interestCents;
    principalPaid += loan.principalPaidCents;
    financingLines.push(line(`${loan.name} — points`, loan.pointsCents, "+"));
    financingLines.push(line(`${loan.name} — fees`, loan.feesCents, "+"));
    financingLines.push(
      line(`${loan.name} — interest over ${holdMonths} months`, loan.interestCents, "+", `on ${fmt(loan.borrowedCents)} borrowed`),
    );
  }
  const financing: Explained = { cents: financingCost, lines: financingLines };

  const usesBeforeSale = acquisition.cents === null ? null : acquisition.cents + z(renovation.cents) + z(holding.cents) + financingCost;
  const cashRequiredRaw = usesBeforeSale === null ? null : usesBeforeSale + principalPaid - capital.borrowedCents;
  const cashRequired: Explained = {
    cents: cashRequiredRaw === null ? null : Math.max(0, cashRequiredRaw),
    lines: [
      line("Acquisition", acquisition.cents, "+"),
      line("Renovation with contingency", renovation.cents, "+"),
      line("Carrying costs while held", holding.cents, "+"),
      line("Financing costs (points, fees, interest)", financingCost, "+"),
      line("Loan principal repaid while held", principalPaid, "+"),
      line("Money borrowed", capital.borrowedCents, "−"),
      ...(cashRequiredRaw !== null && cashRequiredRaw < 0
        ? [line("Borrowed more than the deal uses — no cash required", -cashRequiredRaw, "·")]
        : []),
    ],
  };

  /* ---- flip ---- */
  let flip: FlipResult | null = null;
  const salePrice = a.salePriceCents ?? input.arvCents;
  if (input.strategy === "flip" || salePrice !== null) {
    if (salePrice === null && input.strategy === "flip") missing.push("After-repair value or expected sale price");
    const commission = salePrice === null || a.commissionPctX100 === null ? 0 : mulBps(salePrice, a.commissionPctX100);
    const sellerClosing = salePrice === null || a.sellerClosingPctX100 === null ? 0 : mulBps(salePrice, a.sellerClosingPctX100);
    const disposition: Explained = {
      cents: commission + sellerClosing + z(a.otherDispositionCents),
      lines: [
        line("Commissions", commission, "+"),
        line("Seller closing costs", sellerClosing, "+"),
        line("Other selling costs", z(a.otherDispositionCents), "+"),
      ],
    };
    const total = usesBeforeSale === null ? null : usesBeforeSale + z(disposition.cents);
    const totalProjectCost: Explained = {
      cents: total,
      lines: [
        line("Acquisition", acquisition.cents, "+"),
        line("Renovation with contingency", renovation.cents, "+"),
        line("Financing costs", financingCost, "+"),
        line("Carrying costs", holding.cents, "+"),
        line("Selling costs", disposition.cents, "+"),
      ],
    };
    const profitCents = salePrice === null || total === null ? null : salePrice - total;
    const profit: Explained = {
      cents: profitCents,
      lines: [line("Sale price", salePrice, "+"), line("Total project cost", total, "−")],
    };
    const loanPayoff = capital.loans.reduce((sum, l) => sum + l.balanceAtExitCents, 0);
    const cashBack: Explained = {
      cents: salePrice === null ? null : salePrice - z(disposition.cents) - loanPayoff,
      lines: [line("Sale price", salePrice, "+"), line("Selling costs", disposition.cents, "−"), line("Loans paid off", loanPayoff, "−")],
    };
    const roc = cashRequired.cents ? ratioBps(profitCents, cashRequired.cents) : null;
    const pctSelling = (a.commissionPctX100 ?? 0) + (a.sellerClosingPctX100 ?? 0);
    const breakEvenSale =
      usesBeforeSale === null || pctSelling >= 10000
        ? null
        : Math.ceil(((usesBeforeSale + z(a.otherDispositionCents)) * 10000) / (10000 - pctSelling));
    flip = {
      salePriceCents: salePrice,
      acquisition,
      renovation,
      financing,
      holding,
      disposition,
      totalProjectCost,
      cashRequired,
      profit,
      margin: { bps: ratioBps(profitCents, salePrice), formula: "Profit ÷ sale price" },
      roi: { bps: ratioBps(profitCents, total), formula: "Profit ÷ total project cost" },
      returnOnCash: { bps: roc, formula: "Profit ÷ cash required" },
      annualizedReturnOnCash: {
        bps: annualize(roc, holdMonths),
        formula: `(1 + return on cash)^(12 ÷ ${holdMonths} months) − 1 — for comparing deals of different lengths; not shown above 100% return on cash, where it stops meaning anything`,
      },
      breakEvenSalePriceCents: breakEvenSale,
      breakEvenPurchasePriceCents: purchase === null || profitCents === null ? null : purchase + profitCents,
      cashBackAtSale: cashBack,
      holdMonths,
    };
  }

  /* ---- rental ---- */
  let rental: RentalResult | null = null;
  let refinance: RefinanceResult | null = null;
  let rentalInvestedRaw: Cents | null = null;
  if (input.strategy === "rental" || a.monthlyRentCents !== null) {
    if (a.monthlyRentCents === null && input.strategy === "rental") missing.push("Expected monthly rent");
    const rent = a.monthlyRentCents;
    const scheduled = rent === null ? null : rent * 12;
    const vacancy = scheduled === null || a.vacancyPctX100 === null ? (scheduled === null ? null : 0) : mulBps(scheduled, a.vacancyPctX100);
    const effective = scheduled === null ? null : scheduled - z(vacancy);
    const maintenance = effective === null || a.maintenancePctX100 === null ? 0 : mulBps(effective, a.maintenancePctX100);
    const management = effective === null || a.managementPctX100 === null ? 0 : mulBps(effective, a.managementPctX100);
    const opex =
      z(a.rentalTaxesAnnualCents) + z(a.rentalInsuranceAnnualCents) + maintenance + management + z(a.rentalHoaMonthlyCents) * 12 + z(a.otherOperatingMonthlyCents) * 12;
    const operatingExpenses: Explained = {
      cents: opex,
      lines: [
        line("Property taxes", z(a.rentalTaxesAnnualCents), "+"),
        line("Insurance", z(a.rentalInsuranceAnnualCents), "+"),
        line("Maintenance reserve", maintenance, "+", a.maintenancePctX100 === null ? undefined : `${(a.maintenancePctX100 / 100).toFixed(1)}% of effective rent`),
        line("Management", management, "+", a.managementPctX100 === null ? undefined : `${(a.managementPctX100 / 100).toFixed(1)}% of effective rent`),
        line("HOA (× 12)", z(a.rentalHoaMonthlyCents) * 12, "+"),
        line("Other operating costs (× 12)", z(a.otherOperatingMonthlyCents) * 12, "+"),
      ],
    };
    const noi = effective === null ? null : effective - opex;
    const noiAnnual: Explained = {
      cents: noi,
      lines: [
        line("Scheduled rent (monthly × 12)", scheduled, "+"),
        line("Vacancy", vacancy, "−"),
        line("Operating expenses", opex, "−"),
        line("Debt service is NOT part of NOI", null, "·"),
      ],
    };
    const kept = capital.loans.filter((l) => input.capital.find((s) => s.id === l.sourceId)?.keepAfterRenovation);
    const permanentDebt = kept.reduce((sum, l) => sum + l.balanceAtExitCents, 0);
    const debtService = kept.reduce((sum, l) => sum + l.nextPaymentCents, 0);
    const stillIo = kept.some((l) => l.nextPaymentInterestOnly && l.amortizingPaymentCents !== null);
    const afterIo = stillIo ? kept.reduce((sum, l) => sum + (l.amortizingPaymentCents ?? l.nextPaymentCents), 0) : null;
    const monthlyDebtService: Explained = {
      cents: debtService,
      lines: kept.map((l) =>
        line(`${l.name}${l.nextPaymentInterestOnly ? " (interest only)" : ""}`, l.nextPaymentCents, "+", `balance ${fmt(l.balanceAtExitCents)}`),
      ),
    };
    const monthlyNoi = noi === null ? null : round(noi / 12);
    const cashFlowMonthly = monthlyNoi === null ? null : monthlyNoi - debtService;
    const monthlyCashFlow: Explained = {
      cents: cashFlowMonthly,
      lines: [line("NOI ÷ 12", monthlyNoi, "+"), line("Debt service", debtService, "−")],
    };
    const value = a.stabilizedValueCents ?? input.arvCents;
    if (value === null && input.strategy === "rental") missing.push("Stabilized value (what the rented property would be worth)");
    // A loan that is NOT kept once the property is rented (a temporary
    // private draw, a bridge) has to be repaid, and when the property is
    // held there is no sale to repay it from — so it comes back out of cash.
    const repaid = capital.loans
      .filter((l) => !input.capital.find((s) => s.id === l.sourceId)?.keepAfterRenovation)
      .reduce((sum, l) => sum + l.balanceAtExitCents, 0);
    const investedRaw = cashRequiredRaw === null ? null : cashRequiredRaw + repaid;
    rentalInvestedRaw = investedRaw;
    const cashInvested: Explained = {
      cents: investedRaw === null ? null : Math.max(0, investedRaw),
      lines: [
        ...cashRequired.lines.filter((l) => l.op !== "·"),
        line("Short-term loans repaid when the property is kept", repaid, "+"),
      ],
    };
    const annualCashFlow = cashFlowMonthly === null ? null : cashFlowMonthly * 12;
    const equity: Explained = {
      cents: value === null ? null : value - permanentDebt,
      lines: [line("Stabilized value", value, "+"), line("Loans still owed", permanentDebt, "−")],
    };
    const mgmtMaint = (a.maintenancePctX100 ?? 0) + (a.managementPctX100 ?? 0);
    const vac = a.vacancyPctX100 ?? 0;
    const fixedMonthly =
      (z(a.rentalTaxesAnnualCents) + z(a.rentalInsuranceAnnualCents)) / 12 + z(a.rentalHoaMonthlyCents) + z(a.otherOperatingMonthlyCents);
    const keepShare = ((10000 - vac) / 10000) * ((10000 - mgmtMaint) / 10000);
    rental = {
      scheduledAnnualRentCents: scheduled,
      vacancyAnnualCents: vacancy,
      effectiveAnnualRentCents: effective,
      operatingExpenses,
      noiAnnual,
      capRate: { bps: ratioBps(noi, value), formula: "NOI ÷ stabilized value" },
      permanentDebtCents: permanentDebt,
      monthlyDebtService,
      debtServiceAfterInterestOnlyCents: afterIo,
      monthlyCashFlow,
      annualCashFlowCents: annualCashFlow,
      dscr: { bps: debtService > 0 && monthlyNoi !== null ? ratioBps(monthlyNoi, debtService) : null, formula: "Monthly NOI ÷ monthly debt service" },
      cashInvested,
      cashOnCash: { bps: cashInvested.cents ? ratioBps(annualCashFlow, cashInvested.cents) : null, formula: "Annual cash flow ÷ cash invested" },
      stabilizedValueCents: value,
      stabilizedEquity: equity,
      ltv: { bps: ratioBps(permanentDebt, value), formula: "Loans still owed ÷ stabilized value" },
      breakEvenRentCents: keepShare <= 0 ? null : Math.ceil((fixedMonthly + debtService) / keepShare),
    };

    if (a.refinanceLtvPctX100 !== null && value !== null) {
      const maxDebt = mulBps(value, a.refinanceLtvPctX100);
      const existing = permanentDebt;
      const costs = z(a.refinanceCostsCents);
      const proceeds = maxDebt - existing - costs;
      const newPayment =
        a.refinanceRatePctX100 === null
          ? null
          : a.refinanceAmortizationMonths === null
            ? monthlyInterest(maxDebt, a.refinanceRatePctX100)
            : levelPayment(maxDebt, a.refinanceRatePctX100, a.refinanceAmortizationMonths);
      const cfAfter = monthlyNoi === null || newPayment === null ? null : monthlyNoi - newPayment;
      const cashLeft = cashInvested.cents === null ? null : Math.max(0, cashInvested.cents - Math.max(0, proceeds));
      refinance = {
        ltvPctX100: a.refinanceLtvPctX100,
        stabilizedValueCents: value,
        maximumModeledDebtCents: maxDebt,
        existingDebtCents: existing,
        refinanceCostsCents: costs,
        additionalProceeds: {
          cents: proceeds,
          lines: [
            line(`Maximum modelled debt (${(a.refinanceLtvPctX100 / 100).toFixed(1)}% of value)`, maxDebt, "+"),
            line("Existing secured debt paid off", existing, "−"),
            line("Refinance costs", costs, "−"),
          ],
        },
        newMonthlyPaymentCents: newPayment,
        monthlyCashFlowAfterRefinanceCents: cfAfter,
        cashLeftInDealCents: cashLeft,
        cashOnCashAfterRefinance: {
          bps: cashLeft && cfAfter !== null ? ratioBps(cfAfter * 12, cashLeft) : null,
          formula: "Annual cash flow after refinance ÷ cash left in the deal",
        },
        propertyEquityAfterRefinanceCents: value - maxDebt,
      };
      assumptions.push("A refinance is a model of what a lender might lend at the stated loan-to-value — not an approval.");
    }
  }

  /* ---- maximum allowable offer ---- */
  const heuristicMao =
    input.arvCents === null || a.rehabCents === null ? null : mulBps(input.arvCents, a.heuristicPctX100) - a.rehabCents;
  const targetMao = maximumOffer(input, { flip, rental, purchase, cashRequiredRaw, rentalInvestedRaw });

  const sqft = input.livingAreaSqftX100;
  return {
    engineVersion: DEAL_ENGINE_VERSION,
    strategy: input.strategy,
    missing: [...new Set(missing)],
    assumptions,
    capital,
    flip,
    rental,
    refinance,
    heuristicMaoCents: heuristicMao,
    targetMao,
    rehabPerSqftCents: a.rehabCents === null || !sqft ? null : round((a.rehabCents * 100) / sqft),
  };
}

function maximumOffer(
  input: DealAnalysisInput,
  ctx: { flip: FlipResult | null; rental: RentalResult | null; purchase: Cents | null; cashRequiredRaw: Cents | null; rentalInvestedRaw: Cents | null },
): MaoResult | null {
  const a = input.assumptions;
  if (a.targetValue === null || ctx.purchase === null) return null;
  const kind = a.targetKind;
  const t = a.targetValue;
  const floorOrNull = (value: number): Cents | null => (value < 0 ? null : Math.floor(value));
  const held = "Holds the amounts borrowed and every other assumption as entered.";
  if (kind === "cash-on-cash") {
    const r = ctx.rental;
    if (!r || r.annualCashFlowCents === null || ctx.rentalInvestedRaw === null || t <= 0) return null;
    const other = ctx.rentalInvestedRaw - ctx.purchase;
    return {
      kind,
      target: t,
      maximumPurchasePriceCents: floorOrNull((r.annualCashFlowCents * 10000) / t - other),
      explanation: `Highest price where annual cash flow ÷ cash invested is at least ${(t / 100).toFixed(1)}%. ${held}`,
    };
  }
  const f = ctx.flip;
  if (!f || f.salePriceCents === null || f.totalProjectCost.cents === null || ctx.cashRequiredRaw === null) return null;
  const everythingElse = f.totalProjectCost.cents - ctx.purchase;
  const k = f.salePriceCents - everythingElse;
  switch (kind) {
    case "profit":
      return {
        kind,
        target: t,
        maximumPurchasePriceCents: floorOrNull(k - t),
        explanation: `Highest price that still leaves ${fmt(t)} of profit. ${held}`,
      };
    case "margin":
      return {
        kind,
        target: t,
        maximumPurchasePriceCents: floorOrNull(k - (f.salePriceCents * t) / 10000),
        explanation: `Highest price where profit is at least ${(t / 100).toFixed(1)}% of the sale price. ${held}`,
      };
    case "roi":
      return {
        kind,
        target: t,
        maximumPurchasePriceCents: floorOrNull((k - (everythingElse * t) / 10000) / (1 + t / 10000)),
        explanation: `Highest price where profit ÷ total project cost is at least ${(t / 100).toFixed(1)}%. ${held}`,
      };
    case "cash-return": {
      const other = ctx.cashRequiredRaw - ctx.purchase;
      return {
        kind,
        target: t,
        maximumPurchasePriceCents: floorOrNull((k - (other * t) / 10000) / (1 + t / 10000)),
        explanation: `Highest price where profit ÷ cash required is at least ${(t / 100).toFixed(1)}%. ${held}`,
      };
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Sensitivity (§87.9)                                                 */
/* ------------------------------------------------------------------ */

export type SensitivityVariable = "purchase" | "rehab" | "sale" | "hold" | "rate";

export interface SensitivityRow {
  variable: SensitivityVariable;
  label: string;
  steps: { label: string; profitCents: Cents | null; monthlyCashFlowCents: Cents | null; cashRequiredCents: Cents | null }[];
}

export function sensitivity(input: DealAnalysisInput): SensitivityRow[] {
  const run = (mutate: (copy: DealAnalysisInput) => void) => {
    const copy: DealAnalysisInput = {
      ...input,
      assumptions: { ...input.assumptions },
      capital: input.capital.map((s) => ({ ...s })),
    };
    mutate(copy);
    const result = analyzeDeal(copy);
    return {
      profitCents: result.flip?.profit.cents ?? null,
      monthlyCashFlowCents: result.rental?.monthlyCashFlow.cents ?? null,
      cashRequiredCents: result.flip?.cashRequired.cents ?? result.rental?.cashInvested.cents ?? null,
    };
  };
  const pctSteps = [-1000, -500, 0, 500, 1000];
  const pctLabel = (bps: number) => (bps === 0 ? "As entered" : `${bps > 0 ? "+" : ""}${bps / 100}%`);
  const rows: SensitivityRow[] = [];
  if (input.purchasePriceCents !== null) {
    rows.push({
      variable: "purchase",
      label: "Purchase price",
      steps: pctSteps.map((bps) => ({ label: pctLabel(bps), ...run((c) => (c.purchasePriceCents = c.purchasePriceCents! + mulBps(c.purchasePriceCents!, bps))) })),
    });
  }
  if (input.assumptions.rehabCents !== null) {
    rows.push({
      variable: "rehab",
      label: "Rehab",
      steps: [-1000, 0, 1000, 2000, 3000].map((bps) => ({
        label: pctLabel(bps),
        ...run((c) => (c.assumptions.rehabCents = c.assumptions.rehabCents! + mulBps(c.assumptions.rehabCents!, bps))),
      })),
    });
  }
  const sale = input.assumptions.salePriceCents ?? input.arvCents;
  if (sale !== null) {
    rows.push({
      variable: "sale",
      label: "After-repair value / sale price",
      steps: pctSteps.map((bps) => ({
        label: pctLabel(bps),
        ...run((c) => {
          const next = sale + mulBps(sale, bps);
          c.assumptions.salePriceCents = next;
          c.arvCents = input.arvCents === null ? null : input.arvCents + mulBps(input.arvCents, bps);
        }),
      })),
    });
  }
  if (input.assumptions.holdMonths !== null) {
    const base = input.assumptions.holdMonths;
    rows.push({
      variable: "hold",
      label: "Months held",
      steps: [-2, -1, 0, 2, 4]
        .filter((d) => base + d >= 0)
        .map((d) => ({ label: d === 0 ? "As entered" : `${d > 0 ? "+" : ""}${d} mo`, ...run((c) => (c.assumptions.holdMonths = base + d)) })),
    });
  }
  if (input.capital.some((s) => s.kind !== "cash" && s.ratePctX100 !== null && s.amountCents > 0)) {
    rows.push({
      variable: "rate",
      label: "Interest rates",
      steps: [-100, 0, 100, 200, 300].map((d) => ({
        label: d === 0 ? "As entered" : `${d > 0 ? "+" : ""}${d / 100} pt`,
        ...run((c) => {
          for (const s of c.capital) if (s.kind !== "cash" && s.ratePctX100 !== null) s.ratePctX100 = Math.max(0, s.ratePctX100 + d);
        }),
      })),
    });
  }
  return rows;
}

/* ------------------------------------------------------------------ */

function fmt(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
